import { useTataDigitalBrandPage } from "./hooks/useBrandL2";

import { createAsync, query, useIsRouting, useParams } from "@solidjs/router";
import { Accessor, Match, Show, Suspense, Switch } from "solid-js";
import {
  BrandL2RouteData,
  getBrandL2RouteData,
} from "~/server/data/brand_l2_route_data";
import { DottedLoader } from "~/widgets/loader";
import { HubbleAssured } from "~/components/brand/assured";
import { SecondaryCtas } from "~/components/brand/secondary_ctas";
import { AboutTheBrand } from "~/components/brand_l2/about_amazon";
import { AboutThisVoucher } from "~/components/brand_l2/about_this_card";
import { BrandTitle } from "~/components/brand_l2/brandTitle";
import { DenominationsGrid } from "~/components/denomination_grid/denominations_grid";
import { SimilarBrands } from "~/components/brand_l2/similar_brands";
import { TrustMarkerHappyUsers } from "~/components/brand_l2/trust_marker_happy_users";
import { VoucherGenerationBrandCard } from "~/components/brand_l2/voucher_generation_brand_card";
import { WhatsappTip } from "~/components/brand_l2/whatsapp_tip";
import { Footer } from "~/components/footer";

import { Mode } from "~/types";
import { Divider } from "~/widgets/divider";
import { Spacer } from "~/widgets/spacer";
import { Link, Meta, Title } from "@solidjs/meta";
import { getCategorySlugFromCategoryName } from "~/utils/common";
import { SeoSchema, MetadataTags } from "~/components/brand_l2/seo";
import HubbleImage from "~/widgets/hubble_image";
import BrandL2FooterNotes from "~/components/brand_l2/footer_notes";
import { BreadcrumbsNav } from "~/widgets/breadcrumbs";
import { config } from "~/data/config";

import { getSimulateError } from "~/utils/brandl2";
import { clientRepo } from "~/server/apis/client_repo";
import { InitialSimulationData } from "~/hooks/denominations_grid/useDenominationGridState";
import { clientIdConfig } from "~/data/partner_config";
import { BottomBranding$Client } from "~/components/partner_components/partner_bottom_page_branding";
import { BrandL2Faqs } from "./fragments/faqs";
import DenominationGridHeaderTd from "~/components/partner_components/tata_digital/denomination_grid_header_td";
import { ThreeDotLoader } from "~/widgets/button";
import { useWebStoreContext } from "~/context/webstore_context";
import { SimulatedTransactionDetails } from "~/server/types/brand";

const getBrandL2RouteData$C = query(getBrandL2RouteData, "brand-l2-classic");

export type BrandL2TataDigitalViewProps = {
  brandKey: string;
};

export default function BrandL2TataDigitalView(
  props: BrandL2TataDigitalViewProps
) {
  const routeData = createAsync(() => getBrandL2RouteData$C(props.brandKey), {
    deferStream: true,
  });

  return (
    <Suspense fallback={<DottedLoader />}>
      <Show when={routeData()}>
        <_BrandPageContent
          brandKey={props.brandKey}
          routeData={routeData as Accessor<BrandL2RouteData>}
        />
      </Show>
    </Suspense>
  );
}

function _BrandPageContent(props: {
  brandKey: string;
  routeData: Accessor<BrandL2RouteData>;
}) {
  const brandPageData = useTataDigitalBrandPage({
    brandKey: props.brandKey,
    routeData: props.routeData,
  });

  const params = useParams();

  const isRouting = useIsRouting();

  const {
    routeData,
    getUIConfig,
    getMode,
    isLoggedIn,
    getClientId,
    getMetaTitle,
    onClickGetThisCard,
    getCtaCopy,
    getDenominationList,
    getFaqs,
    showFullPageLoadingOverlay,
    ctaState,
    setCtaState,
    simulateTransactionDetails,
    simulateTransactionDetails2,
  } = brandPageData;

  function getHomePageUrl() {
    return config.paths.homePageUrl;
  }

  function getCategoryPageBasePath() {
    return config.paths.categoryPageBasePath;
  }

  const { storeState } = useWebStoreContext();

  return (
    <>
      <Show when={showFullPageLoadingOverlay() && isRouting()}>
        <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <ThreeDotLoader class="!bg-basePrimaryDark" />
        </div>
      </Show>
      <div class="flex flex-col items-center justify-center gap-6">
        <div class="m-auto mt-3 flex flex-row lg:mt-5 lg:w-[1011px] lg:gap-10">
          <div class="max-w-[573px] lg:ml-0 lg:mr-0">
            <div class="mx-4 flex flex-col gap-0.5 lg:mx-0">
              <Meta name="robots" content="follow, index" />
              <Title>{getMetaTitle()}</Title>
              <Link
                rel="canonical"
                href={`https://offers.myhubble.money/buy-gift-card/${params.brandKey}`}
              />
              <Meta name="og:type" content="product" />
              <Meta name="og:title" content={getMetaTitle()} />
              <Meta
                name="description"
                content={`${routeData()!.brandExtraMetadata.voucherDescription}`}
              />
              <Meta
                name="og:description"
                content={`${routeData()!.brandExtraMetadata.voucherDescription}`}
              />
              <Meta
                name="og:image"
                content={`${routeData()!.brand.voucherProductMetadata.iconImageUrl}`}
              />
              <MetadataTags
                brandName={() => routeData()?.brand.name}
                simulateTransactionDetails={simulateTransactionDetails}
              />
              <SeoSchema
                routeData={routeData}
                simulateTransactionDetails={simulateTransactionDetails}
              />
              <Show
                when={getUIConfig().showBreadcrumbsNav}
                fallback={<Spacer height={30} />}
              >
                <BreadcrumbsNav
                  breadcrumbs={[
                    {
                      label: "HOME",
                      href: getHomePageUrl(),
                    },
                    ...(!config.customizedSDKClients.limitedCategoryContent.includes(
                      routeData()?.clientId!
                    )
                      ? [
                          {
                            label:
                              routeData()!.brandExtraMetadata.categoryTitle?.toUpperCase() ??
                              "SHOP",
                            href: routeData()!.brandExtraMetadata.categoryName
                              ? getCategoryPageBasePath() +
                                getCategorySlugFromCategoryName(
                                  routeData()!.brandExtraMetadata.categoryName
                                )
                              : getHomePageUrl(),
                          },
                        ]
                      : []),
                    {
                      label:
                        routeData()!.brand.voucherProductMetadata.title.toUpperCase(),
                      href: "#",
                      active: true,
                    },
                  ]}
                />
              </Show>
              <BrandTitle
                brandName={routeData()!.brand.voucherProductMetadata.title}
                discountPercentage={
                  simulateTransactionDetails?.discountDetails.hubbleBaseDiscount
                    .percentage!
                }
                showShoppingVoucher={false}
                showPercOff={routeData()!.brand.rewardType != "POSTPAID"}
              />
              <Show
                when={
                  routeData()!.brand.voucherProductMetadata?.headingSubtitle
                }
              >
                <h1 class="line-clamp-1 pt-2 text-normal text-textNormal">
                  {routeData()!.brand.voucherProductMetadata?.headingSubtitle}
                </h1>
              </Show>
            </div>
            <Spacer height={20} />
            <div class="flex flex-col gap-5 lg:gap-6">
              <Show
                when={
                  routeData()!.brand.voucherProductMetadata.isVoucherAssured
                }
              >
                <div class="mx-4 lg:hidden">
                  <HubbleAssured />
                </div>
              </Show>

              <div
                class="flex flex-col gap-4 bg-baseTertiaryMedium p-4 lg:p-0"
                style={
                  routeData()!.brand.name == "Amazon" ||
                  routeData()!.brand.name == "Amazon shopping"
                    ? {
                        "border-radius": "20px",
                      }
                    : { "border-radius": "24px" }
                }
              >
                <Switch>
                  <Match
                    when={
                      routeData()?.brand.voucherProductMetadata.heroImageUrl &&
                      routeData()?.clientId !==
                        clientIdConfig.buddydealsClientId
                    }
                  >
                    <HubbleImage
                      class="aspect-[541/316] w-full rounded-3xl lg:h-[334px]"
                      src={
                        routeData()!.brand.voucherProductMetadata.heroImageUrl
                      }
                      alt={routeData()!.brand.voucherProductMetadata.title}
                    />
                  </Match>
                  <Match when={true}>
                    <VoucherGenerationBrandCard
                      plainLogoUrl={
                        routeData()!.brand.voucherProductMetadata.plainLogoUrl
                      }
                      cardBackgroundColor={
                        routeData()!.brand.voucherProductMetadata
                          .cardBackgroundColor
                      }
                      showHubbleLogo={getUIConfig().showHubbleLogoOnBrandCard}
                    />
                  </Match>
                </Switch>
                <div class="lg:hidden">
                  {buildDenominationGrid({
                    simulateTransactionDetails,
                    simulateTransactionDetails2,
                  })}
                </div>
              </div>

              <div class="mx-4 flex flex-col gap-5 lg:mx-0">
                <SecondaryCtas
                  howToUseSteps={
                    routeData()!.brand.voucherProductMetadata.howToUseSteps
                  }
                  productId={routeData()!.brand.externalId}
                />
                <Show when={getUIConfig().showWhatsappTip}>
                  <div class="lg:hidden">
                    <WhatsappTip />
                  </div>
                </Show>
              </div>

              <div class="mx-[18px] flex flex-col gap-7 lg:mx-2 lg:gap-8">
                <Divider />
                <AboutThisVoucher
                  clientId={getClientId()!}
                  voucherDescription={
                    routeData()!.brandExtraMetadata.voucherDescription
                  }
                  voucherHighlights={
                    routeData()!.brandExtraMetadata.voucherHighlights
                  }
                />
                <Show when={getUIConfig().showHubbleTrustMarker}>
                  <Divider />
                  <TrustMarkerHappyUsers />
                </Show>
                <Show when={routeData()!.brandExtraMetadata.brandDescription}>
                  <Divider />
                  <AboutTheBrand
                    brandName={routeData()!.brand.voucherProductMetadata.title}
                    brandDescription={
                      routeData()!.brandExtraMetadata.brandDescription
                    }
                    thumbnailUrl={
                      routeData()!.brand.voucherProductMetadata.iconImageUrl
                    }
                  />
                </Show>

                <Show when={getFaqs().length > 0}>
                  <Divider />
                  <BrandL2Faqs
                    faqs={getFaqs()}
                    headerText="FAQs"
                    clientId={getClientId()}
                  />
                </Show>
                <Show
                  when={
                    routeData()!.brandExtraMetadata.similarBrands.length > 0 &&
                    getClientId() !== clientIdConfig.tdClientId
                  }
                >
                  <Divider />
                  <SimilarBrands
                    mode={routeData()?.mode as Mode}
                    similarBrands={routeData()!.brandExtraMetadata.similarBrands.slice(
                      0,
                      20
                    )}
                  />
                </Show>
              </div>
            </div>
          </div>

          <div class="hidden lg:flex lg:w-[398px] lg:flex-col">
            <Spacer height={18} />
            <div
              class="gap-5 lg:sticky lg:flex lg:flex-col"
              classList={{
                "lg:top-[92px]":
                  routeData()!.brand.voucherProductMetadata.isVoucherAssured,
                "lg:top-[20px]":
                  !routeData()!.brand.voucherProductMetadata.isVoucherAssured &&
                  !(getClientId() === clientIdConfig.axisClientId),
                "lg:top-10": getClientId() === clientIdConfig.axisClientId,
              }}
            >
              <Show
                when={
                  routeData()!.brand.voucherProductMetadata.isVoucherAssured
                }
                fallback={<Spacer height={52} />}
              >
                <HubbleAssured />
              </Show>
              {buildDenominationGrid({
                simulateTransactionDetails,
                simulateTransactionDetails2,
              })}
              <Show when={getUIConfig().showWhatsappTip}>
                <WhatsappTip />
              </Show>
            </div>
          </div>
        </div>
        <Show when={getUIConfig().showFooterNotes}>
          <BrandL2FooterNotes
            footerList={routeData()!.brandExtraMetadata.footNotes}
          />
        </Show>
        <Show when={getUIConfig().showFooter} fallback={<Spacer height={40} />}>
          <div class="lg:px-40">
            <Footer />
          </div>
        </Show>
        <Show when={getUIConfig().showBrandingOnBottom}>
          <BottomBranding$Client clientId={() => routeData()?.clientId} />
        </Show>
      </div>
    </>
  );

  function getDueAmount(simulatedTransactionDetails: any) {
    if (simulatedTransactionDetails?.paymentSource == "COINS_ONLY") {
      return simulatedTransactionDetails.totalRequiredCoins;
    } else {
      return simulatedTransactionDetails?.dueAmount ?? null;
    }
  }

  function buildDenominationGrid({
    simulateTransactionDetails,
    simulateTransactionDetails2,
  }: {
    simulateTransactionDetails: SimulatedTransactionDetails | null;
    simulateTransactionDetails2: SimulatedTransactionDetails | null;
  }) {
    const initialSimulationData: InitialSimulationData = {
      selectedAmount: simulateTransactionDetails?.redemptionAmount ?? null,
      selectedVariantId:
        routeData()?.brand?.variantDetailedResponse?.find(
          (variant) => variant.isDefault
        )?.variantId ?? "",
      dueAmount: simulateTransactionDetails
        ? getDueAmount(simulateTransactionDetails!)
        : null,
      discountDetails: simulateTransactionDetails?.discountDetails ?? null,
      redemptionAmount: simulateTransactionDetails?.redemptionAmount ?? null,
      dueAmount2: simulateTransactionDetails2
        ? getDueAmount(simulateTransactionDetails2!)
        : null,
      discountDetails2: simulateTransactionDetails2?.discountDetails ?? null,
      redemptionAmount2: simulateTransactionDetails2?.redemptionAmount ?? null,
      hubbleCoinRewards: routeData()?.hubbleCoinRewards ?? null,
      initialErrorOnSimulate: getSimulateError(routeData()?.simulate) ?? null,
      offerConstructTenure: null,
    };

    return (
      <DenominationsGrid
        discountPercentage={
          simulateTransactionDetails?.discountDetails?.totalDiscount
            ?.percentage ?? 0
        }
        brand={routeData()!.brand}
        isCoinsOnly={simulateTransactionDetails?.paymentSource === "COINS_ONLY"}
        allowRewardCoins={getMode() === "hubble" && isLoggedIn}
        showHubbleCoinRewards={
          routeData()?.brand.rewardType === "POSTPAID" && getMode() === "hubble"
        }
        coinsBalance={storeState.coins ?? 0}
        isRewardTypePostPaid={routeData()!.brand.rewardType === "POSTPAID"}
        dividerClass="px-4 lg:px-6"
        minAmount={routeData()!.brand.amountConditions.minAmount}
        maxAmount={routeData()!.brand.amountConditions.maxAmount}
        offerConstructMaxAmount={
          routeData()?.offerConstruct?.oneTimeRewardDetails.maxAmount
        }
        offerConstructAmountUnit={
          routeData()?.offerConstruct?.oneTimeRewardDetails.amountUnit
        }
        offerConstruct={routeData()?.offerConstruct}
        initialSimulationData={initialSimulationData}
        simulateFunction={(params) => clientRepo.simulate(params)}
        initiateOrder={(props) => onClickGetThisCard(props)}
        getMode={getMode}
        getClientId={getClientId}
        ctaCopy={getCtaCopy()}
        denominationList={getDenominationList(routeData()!.brand)}
        buildHeader={getHeader}
        ctaState={ctaState}
        setCtaState={setCtaState}
      />
    );
  }

  function getHeader() {
    if (getClientId() === clientIdConfig.tdClientId) {
      return (
        <DenominationGridHeaderTd
          brandTitle={routeData()!.brand.voucherProductMetadata.title}
          brandLogoUrl={routeData()!.brand.voucherProductMetadata.logoUrl}
        />
      );
    }
  }
}
